/**
 * The YubiKey pool screen — a single flat list, add/edit/delete inline, no detail route.
 *
 * There is nothing on a pool entry that needs a second screen the way a location's operational
 * fields do: five fields, one row, edited in place. `locationId` is entered as a plain number
 * because there is no staff directory equivalent for locations either — the same honesty
 * `SupportRequestView.tsx`'s assignment control practices for staff ids, applied to location ids.
 */
import type { CreateYubikey, UpdateYubikey, Yubikey, YubikeyType } from '@apuestapr/contracts';
import { yubikeyStatuses, yubikeyTypes } from '@apuestapr/contracts';
import {
  Button,
  EmptyState,
  Field,
  Input,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  icons,
  toast,
} from '@apuestapr/ui';
import { useState, type FormEvent, type ReactElement } from 'react';

import { useSessionContext } from '../auth/SessionProvider.tsx';
import { useManagedYubikeys, type ManagedYubikeys } from './hooks.ts';

const YubikeysSkeleton = (): ReactElement => (
  <div
    aria-hidden="true"
    className="overflow-hidden rounded-xl border border-border bg-card p-4 text-card-foreground"
  >
    <Skeleton className="mb-4 h-4 w-48" />
    <div className="grid gap-3">
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="flex items-center gap-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  </div>
);

interface YubikeyRowProps {
  readonly yubikey: Yubikey;
  readonly busy: boolean;
  readonly onUpdate: (id: number, patch: UpdateYubikey) => void;
  readonly onDelete: (id: number) => void;
}

const YubikeyRow = ({ yubikey, busy, onUpdate, onDelete }: YubikeyRowProps): ReactElement => {
  const [locationId, setLocationId] = useState(yubikey.locationId?.toString() ?? '');
  const [status, setStatus] = useState(yubikey.status);
  const [type, setType] = useState<YubikeyType>(yubikey.type);
  const [notes, setNotes] = useState(yubikey.notes ?? '');

  const commitLocation = (): void => {
    const trimmed = locationId.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isInteger(parsed) || parsed <= 0)) return;
    onUpdate(yubikey.id, {
      locationId: parsed,
      status,
      type,
      notes: notes.trim() === '' ? null : notes,
    });
  };

  return (
    <TableRow data-yubikey-id={yubikey.id}>
      <TableHead className="min-w-40 text-foreground" scope="row">
        {yubikey.serialNumber}
      </TableHead>
      <TableCell className="min-w-28">
        <Select
          aria-label={`Estado de ${yubikey.serialNumber}`}
          disabled={busy}
          value={status}
          onChange={(event) => {
            const next = event.currentTarget.value as (typeof yubikeyStatuses)[number];
            setStatus(next);
            onUpdate(yubikey.id, {
              locationId: yubikey.locationId,
              status: next,
              type,
              notes: notes.trim() === '' ? null : notes,
            });
          }}
        >
          {yubikeyStatuses.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
      </TableCell>
      <TableCell className="min-w-28">
        <Select
          aria-label={`Tipo de ${yubikey.serialNumber}`}
          disabled={busy}
          value={type}
          onChange={(event) => {
            const next = event.currentTarget.value as YubikeyType;
            setType(next);
            onUpdate(yubikey.id, {
              locationId: yubikey.locationId,
              status,
              type: next,
              notes: notes.trim() === '' ? null : notes,
            });
          }}
        >
          {yubikeyTypes.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
      </TableCell>
      <TableCell className="min-w-32">
        <Input
          aria-label={`Localidad asignada a ${yubikey.serialNumber}`}
          disabled={busy}
          inputMode="numeric"
          placeholder="Sin asignar"
          value={locationId}
          onChange={(event) => setLocationId(event.currentTarget.value)}
          onBlur={commitLocation}
        />
      </TableCell>
      <TableCell className="min-w-40">
        <Input
          aria-label={`Notas de ${yubikey.serialNumber}`}
          disabled={busy}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
          onBlur={() =>
            onUpdate(yubikey.id, {
              locationId: yubikey.locationId,
              status,
              type,
              notes: notes.trim() === '' ? null : notes,
            })
          }
        />
      </TableCell>
      <TableCell>
        <Button
          disabled={busy}
          onClick={() => onDelete(yubikey.id)}
          size="sm"
          variant="destructive"
        >
          Eliminar <span className="sr-only">{yubikey.serialNumber}</span>
        </Button>
      </TableCell>
    </TableRow>
  );
};

interface Draft {
  readonly serialNumber: string;
  readonly locationId: string;
}

const emptyDraft: Draft = { serialNumber: '', locationId: '' };

const CreateYubikeyForm = ({
  busy,
  serialError,
  onCreate,
}: {
  readonly busy: boolean;
  readonly serialError: string | undefined;
  readonly onCreate: (input: CreateYubikey) => void;
}): ReactElement => {
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (draft.serialNumber.trim() === '') return;
    const trimmedLocation = draft.locationId.trim();
    const locationId = trimmedLocation === '' ? undefined : Number(trimmedLocation);
    if (locationId !== undefined && (!Number.isInteger(locationId) || locationId <= 0)) return;
    onCreate({
      serialNumber: draft.serialNumber,
      ...(locationId === undefined ? {} : { locationId }),
    });
    setDraft(emptyDraft);
  };

  return (
    <form
      className="grid gap-3 rounded-xl border border-dashed border-border p-4 sm:grid-cols-3"
      noValidate
      onSubmit={submit}
    >
      <Field label="Número de serie" controlId="new-yubikey-serial" error={serialError} required>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={draft.serialNumber}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, serialNumber: value }));
            }}
          />
        )}
      </Field>
      <Field
        label="Localidad (opcional)"
        controlId="new-yubikey-location"
        description="Déjalo en blanco para una llave sin asignar en el almacén."
      >
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            inputMode="numeric"
            value={draft.locationId}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, locationId: value }));
            }}
          />
        )}
      </Field>
      <div className="flex items-end">
        <Button disabled={busy} type="submit">
          Añadir YubiKey
        </Button>
      </div>
    </form>
  );
};

const YubikeysView = ({ managed }: { readonly managed: ManagedYubikeys }): ReactElement => {
  const busy = managed.mutation.status === 'loading';
  const serialError =
    managed.mutation.status === 'error' && managed.mutation.failure.kind === 'http'
      ? managed.mutation.failure.details?.find((error) => error.path === 'serialNumber')?.message
      : undefined;

  if (managed.state.status === 'loading') return <YubikeysSkeleton />;
  if (managed.state.status === 'error') {
    return <p role="alert">No se pudo cargar el inventario de llaves YubiKey.</p>;
  }

  const rows = managed.state.data;

  return (
    <>
      {rows.length === 0 ? (
        <EmptyState
          icon={icons.badgeCheck}
          title="Sin llaves YubiKey"
          description="Añade la primera llave al inventario."
        />
      ) : (
        <div
          aria-label="Tabla de llaves YubiKey"
          className="overflow-x-auto rounded-xl border border-border bg-card text-card-foreground"
          role="region"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          <Table>
            <TableCaption>
              {String(rows.length)} {rows.length === 1 ? 'llave' : 'llaves'} en el inventario.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Número de serie</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Localidad</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((yubikey) => (
                <YubikeyRow
                  key={yubikey.id}
                  yubikey={yubikey}
                  busy={busy}
                  onUpdate={(id, patch) => {
                    void managed.update(id, patch).then((result) => {
                      if (result.ok) toast({ title: 'YubiKey actualizada' });
                    });
                  }}
                  onDelete={(id) => {
                    void managed.remove(id).then((result) => {
                      if (result.ok) toast({ title: 'YubiKey eliminada', variant: 'destructive' });
                    });
                  }}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="mt-4">
        <CreateYubikeyForm
          busy={busy}
          serialError={serialError}
          onCreate={(input) => {
            void managed.create(input).then((result) => {
              if (result.ok) toast({ title: 'YubiKey añadida' });
            });
          }}
        />
      </div>
    </>
  );
};

export const YubikeysPage = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const managed = useManagedYubikeys(
    session.client,
    authenticated?.csrfToken ?? '',
    session.handleFailure,
  );

  return (
    <section aria-labelledby="yubikeys-page">
      <div className="mb-4">
        <h2 className="m-0" id="yubikeys-page">
          Llaves YubiKey
        </h2>
        <p className="mb-0 text-muted-foreground">
          El inventario completo, con su localidad asignada si tiene una.
        </p>
      </div>
      <YubikeysView managed={managed} />
    </section>
  );
};
